import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Loader2, Upload, Image, Video, Link, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ContentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  onUploaded: () => void;
}

export function ContentUploadDialog({
  open,
  onOpenChange,
  businessId,
  onUploaded,
}: ContentUploadDialogProps) {
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState(10);
  const [externalUrl, setExternalUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please select an image (JPEG, PNG, GIF, WebP) or video (MP4, WebM)');
      return;
    }

    // Validate file size (max 100MB)
    if (selectedFile.size > 100 * 1024 * 1024) {
      setError('File size must be less than 100MB');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Auto-fill name from filename if empty
    if (!name) {
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');
      setName(fileName);
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  }, [name]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fakeEvent = {
        target: { files: [droppedFile] },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(fakeEvent);
    }
  }, [handleFileChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    if (activeTab === 'upload' && !file) {
      setError('Please select a file to upload');
      return;
    }

    if (activeTab === 'url' && !externalUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    setIsLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      let storagePath: string | null = null;
      let contentType: 'image' | 'video' | 'url' = 'url';

      if (activeTab === 'upload' && file) {
        // Upload file to Supabase Storage
        contentType = file.type.startsWith('video/') ? 'video' : 'image';
        const fileExt = file.name.split('.').pop();
        const fileName = `${businessId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('signage-content')
          .upload(fileName, file, {
            onUploadProgress: (progress) => {
              setUploadProgress(Math.round((progress.loaded / progress.total) * 100));
            },
          });

        if (uploadError) throw uploadError;
        storagePath = fileName;
      }

      // Create content item record
      const { error: insertError } = await supabase
        .from('signage_content_items')
        .insert({
          business_id: businessId,
          name: name.trim(),
          description: description.trim() || null,
          content_type: contentType,
          storage_path: storagePath,
          external_url: activeTab === 'url' ? externalUrl.trim() : null,
          duration_seconds: duration,
          file_size_bytes: file?.size || null,
          mime_type: file?.type || null,
        });

      if (insertError) throw insertError;

      toast.success('Content uploaded successfully');
      onUploaded();
      handleClose();
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveTab('upload');
    setName('');
    setDescription('');
    setDuration(10);
    setExternalUrl('');
    setFile(null);
    setPreview(null);
    setError(null);
    setUploadProgress(0);
    onOpenChange(false);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Content</DialogTitle>
          <DialogDescription>
            Add images, videos, or URLs to your content library
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link className="w-4 h-4" />
                External URL
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 space-y-4">
              {error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {error}
                </div>
              )}

              <TabsContent value="upload" className="mt-0 space-y-4">
                {/* Drop zone */}
                {!file ? (
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => document.getElementById('file-input')?.click()}
                  >
                    <input
                      id="file-input"
                      type="file"
                      className="hidden"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex gap-2">
                        <Image className="w-8 h-8 text-muted-foreground" />
                        <Video className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <p className="font-medium">Drop files here or click to browse</p>
                      <p className="text-sm text-muted-foreground">
                        Images (JPEG, PNG, GIF, WebP) or Videos (MP4, WebM)
                      </p>
                      <p className="text-xs text-muted-foreground">Max 100MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="relative rounded-lg border overflow-hidden">
                    {preview && (
                      file.type.startsWith('video/') ? (
                        <video
                          src={preview}
                          className="w-full h-48 object-cover"
                          muted
                        />
                      ) : (
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-full h-48 object-cover"
                        />
                      )
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={clearFile}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <div className="p-3 bg-muted">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                )}

                {isLoading && uploadProgress > 0 && (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} />
                    <p className="text-sm text-muted-foreground text-center">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="url" className="mt-0 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="external-url">URL</Label>
                  <Input
                    id="external-url"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter a direct URL to an image or video
                  </p>
                </div>
              </TabsContent>

              {/* Common fields */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Summer Promotion Banner"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Display Duration (seconds)</Label>
                <Input
                  id="duration"
                  type="number"
                  min={1}
                  max={300}
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 10)}
                />
                <p className="text-xs text-muted-foreground">
                  How long to show this content in playlists (1-300 seconds)
                </p>
              </div>
            </div>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {activeTab === 'upload' ? 'Upload' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
